import { useState } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { createLogger } from '../utils/logger';
import { estimateRequestResponseTokens } from '../utils/tokenEstimator';

const logger = createLogger({ module: 'useTalkingPoints' });

export interface TalkingPoint {
  format: 'STAR';
  situation: string;
  task: string;
  action: string;
  result: string;
  fullSuggestion: string;
  isPrompt: boolean;
}

export interface GuidanceSuggestion {
  promptType: 'reflection-question' | 'evidence-prompt' | 'action-item';
  content: string;
  examples?: string[];
}

export type TalkingPointsResponse = 
  | { 
      type: 'evidence-based'; 
      points: TalkingPoint[]; 
      evidenceLevel: 'sparse' | 'good';
      disclaimer: string;
      tips: string[];
    }
  | { 
      type: 'guidance'; 
      suggestions: GuidanceSuggestion[]; 
      evidenceLevel: 'none';
      disclaimer: string;
      tips: string[];
    }
  | {
      // Error response type
      type?: 'evidence-based';
      points?: TalkingPoint[];
      evidenceLevel: 'error';
      disclaimer: string;
      tips: string[];
    };

interface GenerateTalkingPointsParams {
  rubricCriterion: {
    id: string;
    category: string;
    description: string;
  };
  evidenceCards: Array<{
    title: string;
    summary?: string;
    impact?: string;
    card_data?: any;
  }>;
  userContext: {
    focusTitle: string;
    companyName: string;
    industryType: string;
    currentWeek: number;
    totalWeeks: number;
    teamDescription?: string;
    primaryResponsibilities?: string[];
    reflections?: {
      biggestWin?: string;
      keyLearnings?: string;
      challengesOvercome?: string;
    };
  };
}

export const useTalkingPoints = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPoints, setGeneratedPoints] = useState<TalkingPointsResponse | null>(null);

  const generateTalkingPoints = async (params: GenerateTalkingPointsParams) => {
    if (!user) {
      showToast('Please sign in to generate talking points', 'error');
      return null;
    }

    setIsGenerating(true);
    setGeneratedPoints(null);

    try {
      logger.info('Generating talking points', {
        criterionId: params.rubricCriterion.id,
        evidenceCount: params.evidenceCards.length,
        industry: params.userContext.industryType
      });

      const { data, error } = await supabase.functions.invoke('generate-talking-points', {
        body: params
      });

      if (error) {
        throw error;
      }

      if (!data) {
        throw new Error('Invalid response from talking points generator');
      }

      // Type guard to check if it's an error response
      if ('evidenceLevel' in data && data.evidenceLevel === 'error') {
        throw new Error(data.disclaimer || 'Failed to generate talking points');
      }

      // Validate response has required fields based on type
      if (data.type === 'guidance' && !data.suggestions) {
        throw new Error('Invalid guidance response from talking points generator');
      } else if (data.type === 'evidence-based' && !data.points) {
        throw new Error('Invalid evidence-based response from talking points generator');
      }

      setGeneratedPoints(data);
      
      // Track usage for analytics with better token estimation
      try {
        const tokenUsage = estimateRequestResponseTokens(params, data, 'gpt-4.1-mini');
        
        await supabase.from('ai_usage_logs').insert({
          user_id: user.id,
          function_name: 'generate-talking-points',
          input_tokens: tokenUsage.inputTokens,
          output_tokens: tokenUsage.outputTokens,
          model: 'gpt-4.1-mini',
          context: {
            criterion_id: params.rubricCriterion.id,
            evidence_count: params.evidenceCards.length,
            evidence_level: data.evidenceLevel,
            response_type: data.type,
            estimation_method: tokenUsage.method
          }
        });
      } catch (err) {
        // Don't fail if analytics fails
        logger.error('Failed to log AI usage', err);
      }

      const successMessage = data.evidenceLevel === 'none' 
        ? '💡 Guidance generated successfully!' 
        : '✨ Talking points generated successfully!';
      showToast(successMessage, 'success');
      return data;
    } catch (error) {
      logger.error('Failed to generate talking points', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate talking points';
      showToast(errorMessage, 'error');
      
      // Return error response
      const errorResponse = {
        type: 'evidence-based' as const,
        points: [],
        evidenceLevel: 'error' as const,
        disclaimer: 'Unable to generate talking points. Please try again.',
        tips: []
      };
      
      setGeneratedPoints(errorResponse);
      return errorResponse;
    } finally {
      setIsGenerating(false);
    }
  };

  const clearGeneratedPoints = () => {
    setGeneratedPoints(null);
  };

  const saveTalkingPoint = async (
    focusId: string, 
    rubricItemId: string, 
    talkingPoint: TalkingPoint
  ) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('review_prep_talking_points')
        .upsert({
          focus_id: focusId,
          rubric_item_id: rubricItemId,
          user_id: user.id,
          talking_point: talkingPoint,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'focus_id,rubric_item_id,user_id'
        });

      if (error) throw error;

      showToast('Talking point saved!', 'success');
      return true;
    } catch (error) {
      logger.error('Failed to save talking point', error);
      showToast('Failed to save talking point', 'error');
      return false;
    }
  };

  const getSavedTalkingPoints = async (focusId: string, rubricItemId?: string) => {
    if (!user) return [];

    try {
      let query = supabase
        .from('review_prep_talking_points')
        .select('*')
        .eq('focus_id', focusId)
        .eq('user_id', user.id);

      if (rubricItemId) {
        query = query.eq('rubric_item_id', rubricItemId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data || [];
    } catch (error) {
      logger.error('Failed to fetch saved talking points', error);
      return [];
    }
  };

  return {
    generateTalkingPoints,
    isGenerating,
    generatedPoints,
    clearGeneratedPoints,
    saveTalkingPoint,
    getSavedTalkingPoints
  };
};